import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { revealGatewayKey, paymentError } from "../api/payments";

/** Holds explicitly revealed credentials in memory for at most one minute, never in storage or drafts. */
export default function useGatewayKeyReveal(version, reader = revealGatewayKey) {
  const token = useSelector((state) => state.user.token);
  const [keys, setKeys] = useState({}), [busy, setBusy] = useState(""), [error, setError] = useState("");
  const generation = useRef(0), pending = useRef(false);
  const timers = useRef({});
  useEffect(() => {
    const activeTimers = timers.current;
    const requestGeneration = generation;
    generation.current++; pending.current = false; setKeys({}); setBusy(""); setError("");
    return () => { requestGeneration.current++; Object.values(activeTimers).forEach(clearTimeout); };
  }, [token, version, reader]);
  async function toggle(environment) {
    if (Object.hasOwn(keys, environment)) {
      clearTimeout(timers.current[environment]);
      setKeys((current) => { const copy = { ...current }; delete copy[environment]; return copy; }); return;
    }
    if (pending.current) return;
    const current = generation.current; pending.current = true; setBusy(environment); setError("");
    try {
      const key = await reader(token, environment);
      if (current === generation.current) {
        setKeys((value) => ({ ...value, [environment]: key }));
        timers.current[environment] = setTimeout(() => setKeys((value) => {
          const next = { ...value }; delete next[environment]; return next;
        }), 60000);
      }
    } catch (reason) { if (current === generation.current) setError(paymentError(reason)); }
    finally { if (current === generation.current) { pending.current = false; setBusy(""); } }
  }
  return { keys, busy, error, toggle };
}
